// Effect HUD + screen tint + night vision (MB.4). PURE (ENG-1). Active status
// effects wash the screen with a blended tint (their Java particle colours) and
// night vision floors the world brightness. The client reads these to draw the
// overlay + HUD swatches and to lift the daylight uniform.
import { hasEffect, type ActiveEffect } from './effects';

/** RGB (0..255) each effect tints the screen with. */
export const EFFECT_COLOR: Readonly<Record<string, [number, number, number]>> = {
  speed: [0x7c, 0xaf, 0xc6], slowness: [0x5a, 0x6c, 0x81],
  haste: [0xd9, 0xc0, 0x43], strength: [0x93, 0x24, 0x23], weakness: [0x48, 0x4d, 0x48],
  poison: [0x4e, 0x93, 0x31], wither: [0x35, 0x2a, 0x27],
  regeneration: [0xcd, 0x5c, 0xab], fire_resistance: [0xe4, 0x9a, 0x3a],
  water_breathing: [0x2e, 0x52, 0x99], night_vision: [0x1f, 0x1f, 0xa1],
  invisibility: [0x7f, 0x83, 0x92], resistance: [0x99, 0x45, 0x3a], absorption: [0x25, 0x52, 0xa5],
  jump_boost: [0x22, 0xff, 0x4c], slow_falling: [0xf3, 0xcf, 0xb3],
  instant_health: [0xf8, 0x24, 0x23], instant_damage: [0x43, 0x0a, 0x09],
};

/** Per-effect alpha step and the overall cap for the blended wash. */
export const TINT_PER_EFFECT = 0.12;
export const TINT_MAX_ALPHA = 0.4;

/** A blended screen overlay {r,g,b,a} from the active effects (a=0 ⇒ none). The
 *  colour is the average of the active effects' colours; alpha grows with how many
 *  are active, capped so it stays a wash. */
export function screenTint(effects: readonly ActiveEffect[]): { r: number; g: number; b: number; a: number } {
  let r = 0, g = 0, b = 0, n = 0;
  for (const e of effects) {
    const c = EFFECT_COLOR[e.id];
    if (!c) continue;
    r += c[0]; g += c[1]; b += c[2]; n++;
  }
  if (n === 0) return { r: 0, g: 0, b: 0, a: 0 };
  return { r: Math.round(r / n), g: Math.round(g / n), b: Math.round(b / n), a: Math.min(TINT_MAX_ALPHA, n * TINT_PER_EFFECT) };
}

/** Daylight floor while night vision is active — a fully-lit view of caves/night. */
export const NIGHT_VISION_FLOOR = 1;
export function nightVisionFloor(effects: readonly ActiveEffect[]): number {
  return hasEffect(effects, 'night_vision') ? NIGHT_VISION_FLOOR : 0;
}

/** The HUD swatch list: one {id, amplifier, ticks, color} per active effect. */
export function effectHud(effects: readonly ActiveEffect[]): Array<{ id: string; amplifier: number; ticks: number; color: [number, number, number] }> {
  return effects.map((e) => ({ id: e.id, amplifier: e.amplifier, ticks: e.ticks, color: EFFECT_COLOR[e.id] ?? [0xff, 0xff, 0xff] }));
}
